import { useState, useEffect } from 'react'
import { Container, Row, Col, Table, Modal, Form, Button, Alert, FloatingLabel } from 'react-bootstrap'
import useTableData from '../hooks/useTableData'
import Notification from '../components/Notification'
import { forceLogout, shouldForceLogout } from '../services/auth/session'

const passwordRules = [
  { id: 'length', label: 'Minimálně 8 znaků', test: (value) => value.length >= 8 },
  { id: 'lower', label: 'Alespoň jedno malé písmeno', test: (value) => /[a-z]/.test(value) },
  { id: 'upper', label: 'Alespoň jedno velké písmeno', test: (value) => /[A-Z]/.test(value) },
  { id: 'number', label: 'Alespoň jedno číslo', test: (value) => /\d/.test(value) },
  { id: 'symbol', label: 'Alespoň jeden symbol', test: (value) => /[^A-Za-z0-9]/.test(value) },
]

const evaluatePasswordRules = (password = '') =>
  passwordRules.map((rule) => ({ ...rule, ok: rule.test(password) }))

const isPasswordPolicyValid = (checks) => checks.every((rule) => rule.ok)

const Users = ({ tabActive }) => {
  const authFetch = async (url, options = {}) => {
    const response = await fetch(url, {
      credentials: 'include',
      ...options,
      headers: {
        ...(options.headers || {})
      }
    })
    if (shouldForceLogout(response.status, url)) {
      forceLogout()
    }
    return response
  }
  const [uzivatele, setUzivatele] = useState([])
  const [role, setRole] = useState(null)
  const [stav, setStav] = useState(true)

  const [isModalInfoOpen, setIsModalInfoOpen] = useState(false)
  const [isModalAddOpen, setIsModalAddOpen] = useState(false)
  const [isModalPasswordOpen, setIsModalPasswordOpen] = useState(false)
  const [isModalUpdateOpen, setIsModalUpdateOpen] = useState(false)
  const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false)
  const [isError, setIsError] = useState(false)
  const [validated, setValidated] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const [notifications, setNotifications] = useState([])
  const [uzivatelMrp, setUzivatelMrp] = useState('')
  const [addPassword, setAddPassword] = useState('')
  const [changePassword, setChangePassword] = useState('')
  const [showAddPassword, setShowAddPassword] = useState(false)
  const [showChangePassword, setShowChangePassword] = useState(false)

  const addPasswordChecks = evaluatePasswordRules(addPassword)
  const changePasswordChecks = evaluatePasswordRules(changePassword)
  const isAddPasswordValid = isPasswordPolicyValid(addPasswordChecks)
  const isChangePasswordValid = isPasswordPolicyValid(changePasswordChecks)

  // Načtení dat po změně tabu
  useEffect(() => {
    fetchData()
  }, [tabActive])

  // Uživatelské role
  const roleLabels = {
    1: 'admin',
    2: 'technik',
    3: 'mechanik',
  }

  // Načtení dat
  const fetchData = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_API_URL}/uzivatele`, {
        credentials: 'include'
      })
      const list = await response.json()
      setUzivatele(Array.isArray(list) ? list : [])
    } catch (error) {
      console.error('Chyba při načítání dat:', error)
      setUzivatele([])
    }
  }

  // Modální okna
  const openInfoModal = () => {
    setIsModalInfoOpen(true)
  }
  const openAddModal = () => {
    setStav(true)
    setRole(1)
    setUzivatelMrp('')
    setAddPassword('')
    setShowAddPassword(false)
    setIsModalAddOpen(true)
  }
  const openPasswordModal = async (id_uzivatele, uzivatel) => {
    setChangePassword('')
    setShowChangePassword(false)
    setIsModalPasswordOpen(true)
    setSelectedItem({ id_uzivatele, uzivatel })    
  }
  const openUpdateModal = async (id_uzivatele, jmeno, email, uzivatel, role, stav, uzivatel_mrp) => {
    setStav(stav)
    setRole(role)
    setUzivatelMrp(Number(role) === 3 ? '' : (uzivatel_mrp || ''))
    if (Number(role) === 3) {
      setSelectedItem({ id_uzivatele, jmeno, email, uzivatel, role, stav, uzivatel_mrp: '' })
    } else {
      setSelectedItem({ id_uzivatele, jmeno, email, uzivatel, role, stav, uzivatel_mrp })
    }
    setIsModalUpdateOpen(true)
  }
  const openDeleteModal = async (id_uzivatele, jmeno, email, uzivatel, role, stav, uzivatel_mrp) => {
    setIsModalDeleteOpen(true)
    setSelectedItem({ id_uzivatele, jmeno, email, uzivatel, role, stav, uzivatel_mrp })
  }
  const closeModal = (akce) => {
    setIsModalInfoOpen(false)
    setIsModalAddOpen(false)
    setIsModalPasswordOpen(false)
    setIsModalUpdateOpen(false)
    setIsModalDeleteOpen(false)
    setIsError(false)
    setValidated(false)
    setSelectedItem(null)
    setUzivatelMrp('')
    setAddPassword('')
    setChangePassword('')
    setShowAddPassword(false)
    setShowChangePassword(false)
    // Zobrazení notifikace
    if(akce === 'create' || akce === 'update' || akce === 'delete') {
      const newNotification = {
        id: Date.now(),
        message: akce === 'create' ? 'Záznam byl uložen.' : akce === 'update' ? 'Záznam byl upraven' : akce === 'delete' ? 'Záznam byl odstraněn' : '',
        time: new Date().toLocaleTimeString(),
      }
      setNotifications(prev => [...prev, newNotification])  
    }    
  }

  // Přidání záznamu
  const handleAddItem = async (e) => {
    e.preventDefault()    
    const form = e.currentTarget
    const isValid = form.checkValidity()
    setValidated(true)
    if (!isValid || !isAddPasswordValid) {
      return
    }
    const formData = new FormData(form)
    const jmeno = formData.get('jmeno')    
    const email = formData.get('email')
    const uzivatel = formData.get('uzivatel')
    const role = Number(formData.get('role'))
    const uzivatel_mrp = role === 3 ? '' : uzivatelMrp
    const heslo = formData.get('heslo')
    const stav = Number(formData.get('stav') === 'on')
    const newItem = { jmeno, email, uzivatel, heslo, role, stav, uzivatel_mrp }
    try {
      const response = await authFetch(`${process.env.REACT_APP_API_URL}/uzivatele/overit?jmeno=${newItem.jmeno}&email=${newItem.email}&uzivatel=${newItem.uzivatel}&uzivatel_mrp=${newItem.uzivatel_mrp}`, {
        credentials: 'include'
      })
      const data = await response.json()
      if (data.exists) {
        setIsError(true)
        return
      }
      const addResponse = await authFetch(`${process.env.REACT_APP_API_URL}/uzivatele`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newItem),
      })
      if (!addResponse.ok) {
        const errorResponse = await response.json()
        throw new Error(errorResponse.message)
      }
      fetchData()
      closeModal('create')      
    } catch (error) {
      console.error('Chyba při ukládání záznamu:', error)
      setIsError(true)
    }
  }

  // Změna hesla
  const handlePasswordItem = async (e) => {
    e.preventDefault()    
    const form = e.currentTarget
    const isValid = form.checkValidity()
    setValidated(true)
    if (!isValid || !isChangePasswordValid) {
      return
    }
    const formData = new FormData(form)
    const heslo = formData.get('heslo')
    try {
      const updateResponse = await authFetch(`${process.env.REACT_APP_API_URL}/uzivatele/heslo/${selectedItem.id_uzivatele}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          heslo: heslo
        })
      }) 
      if (!updateResponse.ok) {
        throw new Error('Nepodařilo se upravit záznam.')
      }   
      fetchData()
      closeModal('update')  
    } catch (error) {
      console.error('Chyba při ukládání záznamu:', error)
    } 
  }
  
  // Změna záznam
  const handleUpdateItem = async (e) => {
    e.preventDefault()    
    const form = e.currentTarget
    const isValid = form.checkValidity()
    setValidated(true)
    if (!isValid) {
      return
    }
    const formData = new FormData(form)
    const jmeno = formData.get('jmeno')
    const email = formData.get('email')
    const uzivatel = formData.get('uzivatel')
    const role = Number(formData.get('role'))
    const uzivatel_mrp = role === 3 ? '' : uzivatelMrp
    const stav = Number(formData.get('stav') === 'on')
    const newItem = { jmeno, email, uzivatel, role, stav, uzivatel_mrp }
    try {
      const response = await authFetch(`${process.env.REACT_APP_API_URL}/uzivatele/overit/id/${selectedItem.id_uzivatele}?jmeno=${newItem.jmeno}&email=${newItem.email}&uzivatel=${newItem.uzivatel}&uzivatel_mrp=${newItem.uzivatel_mrp}`, {
        credentials: 'include'
      })
      const data = await response.json()  
      if (data.exists) {
        setIsError(true)
        return
      } 
      const updateUzivatele = await authFetch(`${process.env.REACT_APP_API_URL}/uzivatele/${selectedItem.id_uzivatele}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(newItem)
      }) 
      if (!updateUzivatele.ok) {
        throw new Error('Nepodařilo se upravit záznam.')
      }  
      fetchData()
      closeModal('update')
    } catch (error) {
      console.error('Chyba při úpravě záznamu:', error)
    }
  }

  // Smazání záznamu
  const handleDeleteItem = async (e) => {
    e.preventDefault()
    try {
      await authFetch(`${process.env.REACT_APP_API_URL}/uzivatele/${selectedItem.id_uzivatele}`, {
        method: 'DELETE',
        credentials: 'include'
      })       
      fetchData()
      closeModal('delete')  
    } catch (error) {
      console.error('Chyba při odstraňování záznamu:', error)
    }
  }

  // Props pro hook řazení a stránkování
  const table = useTableData(uzivatele, {
    defaultSortField: 'role',
    defaultSortDirection: 'asc',
    searchableFields: ['id_uzivatele', 'jmeno', 'email', 'uzivatel', 'role', 'stav', 'uzivatel_mrp']
  })

  const handleRoleChange = (nextRole) => {
    const parsedRole = Number(nextRole)
    setRole(parsedRole)
    if (parsedRole === 3) {
      setUzivatelMrp('')
    }
  }

  return (
    <> 
      {/* Hledání */}
      <div className="row d-flex align-items-center justify-content-space my-4">
        <div className='col'>
        <button 
          className="btn btn-primary" 
          title='Nový záznam' 
          onClick={() => openAddModal()}
        >
          <i className="bi bi-plus-circle me-2"></i>Přidat
        </button>
        <button 
          className="btn btn-secondary ms-1" 
          title='Poznámky' 
          onClick={() => openInfoModal()}
        >
          <i className="bi bi-info-circle"></i>
        </button>
        </div>
        <div className='col'>
            <div className="search-wrapper">
            <span className="search-icon"><i className="bi bi-search"></i></span>
            <input
              type="text"
              placeholder="Hledat..."
              value={table.searchQuery}
              onChange={(e) => table.setSearchQuery(e.target.value)}
            />
            {table.searchQuery && (            
              <span 
                className="clear-icon" 
                onClick={() => table.setSearchQuery('')}
              >
                <i className="bi bi-x"></i>
              </span>        
            )}
          </div> 
        </div>            
      </div> 
      {/* Tabulka */}
      <div className='row my-3'>
        <div className='col'>
          <Table responsive hover className='table-transparent'>
            <thead>
              <tr>          
                <th onClick={() => table.handleSort('role')}>role <i className={`bi ${table.sortField === 'role' ? (table.sortDirection === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill') : 'invisible-icon'}`}></i></th>
                <th onClick={() => table.handleSort('jmeno')}>jméno <i className={`bi ${table.sortField === 'jmeno' ? (table.sortDirection === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill') : 'invisible-icon'}`}></i></th>      
                <th onClick={() => table.handleSort('email')}>email <i className={`bi ${table.sortField === 'email' ? (table.sortDirection === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill') : 'invisible-icon'}`}></i></th>
                <th onClick={() => table.handleSort('uzivatel')}>uživatel <i className={`bi ${table.sortField === 'uzivatel' ? (table.sortDirection === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill') : 'invisible-icon'}`}></i></th>
                <th onClick={() => table.handleSort('uzivatel_mrp')}>MRP <i className={`bi ${table.sortField === 'uzivatel_mrp' ? (table.sortDirection === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill') : 'invisible-icon'}`}></i></th>
                <th onClick={() => table.handleSort('stav')}>stav <i className={`bi ${table.sortField === 'stav' ? (table.sortDirection === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill') : 'invisible-icon'}`}></i></th>                                
                <th>akce</th>
              </tr>
            </thead>
            <tbody>
              {table.data.length > 0 ? (
                table.data.map(({ id_uzivatele, jmeno, email, uzivatel, role, stav, uzivatel_mrp }) => (
                  <tr 
                    key={id_uzivatele}
                    onDoubleClick={() => openUpdateModal(id_uzivatele, jmeno, email, uzivatel, role, stav, uzivatel_mrp)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>{roleLabels[role] || 'neznámá role'}</td>
                    <td>{jmeno}</td>
                    <td>{email}</td>
                    <td>{uzivatel}</td>                   
                    <td>{uzivatel_mrp?? '—'}</td>                   
                    <td>{stav ? 'aktivní' : 'blokovaný'}</td>                    
                    <td>
                      <button 
                        className='btn-table' 
                        onClick={() => openPasswordModal(id_uzivatele, uzivatel)} 
                        title='Změna hesla'
                      >
                        <i className="bi bi-shield-lock"></i>
                      </button>
                      <button 
                        className='btn-table' 
                        onClick={() => openUpdateModal(id_uzivatele, jmeno, email, uzivatel, role, stav, uzivatel_mrp)} 
                        title='Upravit'
                      >
                        <i className="bi bi-pencil-square"></i>
                      </button>
                      <button 
                        className='btn-table' 
                        onClick={() => openDeleteModal(id_uzivatele, jmeno, email, uzivatel, role, stav, uzivatel_mrp)} 
                        title='Odstranit'
                        disabled={id_uzivatele === 1}
                      >
                        <i className="bi bi-trash"></i>
                      </button>                            
                    </td>
                  </tr>
                ))
              ) : (
                <tr className='no-data-row'>
                  <td colSpan="7" className='border-0'>
                    <div className='d-block my-3 text-center'>
                      <i className="bi bi-journal-text fs-4"></i>
                      <p className='small'>Nebyly nalezeny žádné údaje.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>

      {/* Stránkování */}
      <div className="row d-flex align-items-center justify-content-between my-2">
        <div className="col pagination small">
          {table.totalItems > 0 && (
            <>
            <span>{table.startIndex + 1} – {Math.min(table.endIndex, table.totalItems)} z {table.totalItems}</span>
            <button className="btn btn-sm border-0 p-0" onClick={table.handlePrevPage} disabled={table.currentPage === 1}><i className="bi bi-chevron-left"></i></button>
            <button className="btn btn-sm border-0 p-0" onClick={table.handleNextPage} disabled={table.currentPage === table.totalPages}><i className="bi bi-chevron-right"></i></button>
            <select value={table.itemsPerPage} onChange={(e) => table.setItemsPerPage(Number(e.target.value))}>
              <option value={15}>15</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            </>
          )}
        </div>
      </div>

      {/* Modalní okna */}
      {isModalInfoOpen && (
        <Modal show={isModalInfoOpen} fullscreen="md-down" onHide={closeModal} keyboard={true} size="md" aria-labelledby="contained-modal-title-vcenter" centered>
          <Modal.Header closeButton className='border-0 px-4'>
            <Modal.Title className='text-primary mt-3 lh-1'>Uživatelé<br/>
              <span className='text-muted fs-6'>poznámky</span>
            </Modal.Title>            
          </Modal.Header>
          <Form noValidate validated={validated} onSubmit={handleAddItem}>
            <Modal.Body>              
              <Container className='px-2'>
                <Row className='g-2'>
                  <Col xs={12}>
                    <p>Správa uživatelských přístupů. Uživatelé, na které se odkazují jiné záznamy v databázi nelze odstranit. Při vkládání nebo změnách záznamů se nesmí jméno, email, uživatelské jméno v MRP nebo uživatelské jméno do aplikace opakovat. Při změně hesla nebo stavu bude uživatel během 5 minut automaticky odhlášen. Pro mechanika nelze vybrat uživatelské jméno v MRP (žádné nemá).</p>
                  </Col>                   
                </Row>             
              </Container>              
            </Modal.Body>
            <Modal.Footer className='border-0 d-flex justify-content-start px-4 pb-3'>
              <Button className='mx-0 my-2' variant="secondary" onClick={closeModal}>Zavřít</Button>            
            </Modal.Footer>
          </Form>
        </Modal>   
      )}
      {isModalAddOpen && (
        <Modal show={isModalAddOpen} fullscreen="md-down" onHide={closeModal} keyboard={true} size="md" aria-labelledby="contained-modal-title-vcenter" centered scrollable>
          <Modal.Header closeButton className='border-0 px-4'>
            <Modal.Title className='text-primary mt-3 lh-1'>Uživatel<br/>
              <span className='text-muted fs-6'>přidat nový záznam</span>
            </Modal.Title>            
          </Modal.Header>
          <Form noValidate validated={validated} onSubmit={handleAddItem} className='overflow-auto' autoComplete="off">
            <Modal.Body> 
              <Container className='px-2'>
                <Row className='g-1'>                              
                  <Col xs={12}>
                    <Form.Group controlId="formJmeno">
                      <FloatingLabel
                        label='jméno a příjmení'>
                        <Form.Control
                          type="text"
                          name='jmeno'
                          placeholder="Jméno a příjmení"
                          maxLength={64}
                          title='Uveď jméno a příjmení uživatele (max. 64 znaků).'
                          required
                        />
                      </FloatingLabel>
                    </Form.Group>
                  </Col>
                  <Col xs={12}>
                    <Form.Group controlId="formEmail">
                      <FloatingLabel label='email'>
                        <Form.Control
                          type="email"
                          name='email'
                          placeholder="Email"
                          maxLength={64}
                          onBlur={(e) => {
                            e.target.value = e.target.value
                              .replace(/\s+/g, '')  
                              .toLowerCase()        
                          }}
                          title='Uveď emailovou adresu uživatele (max. 64 znaků).'
                          required
                        />
                      </FloatingLabel>
                    </Form.Group>
                  </Col>
                  <Col xs={12}>
                    <Form.Group controlId="formMRP">
                      <FloatingLabel label='uživatelské jméno MRP'>
                        <Form.Control
                          type="text"
                          name='uzivatel_mrp'
                          placeholder="uživatel MRP"                          
                          maxLength={20}
                          autoComplete="off"
                          value={uzivatelMrp}
                          readOnly={Number(role) === 3}
                          disabled={Number(role) === 3}
                          onChange={(e) => setUzivatelMrp(e.target.value)}
                          onBlur={(e) => {
                            const formatted = e.target.value
                              .replace(/\s+/g, '')
                              .toUpperCase()
                            setUzivatelMrp(formatted)
                          }}
                          title='Uveď uživatelské jméno v MRP (max. 20 znaků).'
                          required={Number(role) !== 3}
                        />
                      </FloatingLabel>
                    </Form.Group>
                  </Col>
                  <Col xs={12}>
                    <Form.Group controlId="formUzivatel">
                      <FloatingLabel label='uživatelské jméno'>
                        <Form.Control
                          type="text"
                          name='uzivatel'
                          placeholder="Uživatelské jméno"
                          maxLength={32}
                          autoComplete="off"
                          onBlur={(e) => {
                            e.target.value = e.target.value
                              .replace(/\s+/g, '')   
                              .toLowerCase()         
                          }}
                          title='Uveď uživatelské jméno do aplikace (max. 32 znaků).'
                          required
                        />
                      </FloatingLabel>
                    </Form.Group>
                  </Col>                  
                  <Col xs={12}>
                    <Form.Group controlId="formHeslo">
                      <div className="input-group has-validation password-input-group">
                        <FloatingLabel label='heslo' className="flex-grow-1">
                          <Form.Control
                            type={showAddPassword ? 'text' : 'password'}
                            name='heslo'
                            placeholder="Heslo"
                            minLength={8}
                            maxLength={64}
                            autoComplete="new-password"
                            value={addPassword}
                            onChange={(e) => setAddPassword(e.target.value)}
                            className={validated && !isAddPasswordValid ? "is-invalid start-pill" : "start-pill"}
                            title='Uveď heslo: min. 8 znaků, malé i velké písmeno, číslo a symbol.'
                            required
                          />
                        </FloatingLabel>
                        <button
                          type="button"
                          className="btn btn-outline-secondary password-toggle-group-btn end-pill"
                          onClick={() => setShowAddPassword((prev) => !prev)}
                          aria-label={showAddPassword ? 'Skrýt heslo' : 'Zobrazit heslo'}
                        >
                          <i className={`bi ${showAddPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                        </button>
                        <div className="invalid-feedback">Heslo musí splňovat všechny podmínky.</div>
                      </div>
                    </Form.Group>
                    <div className="password-checklist mt-2">
                      {addPasswordChecks.map((rule) => (
                        <div key={rule.id} className={`password-check-item ${rule.ok ? 'is-ok' : 'is-pending'}`}>
                          <i className={`bi ${rule.ok ? 'bi-check-circle-fill' : 'bi-x-circle'}`}></i>
                          <span>{rule.label}</span>
                        </div>
                      ))}
                    </div>
                  </Col>
                   <Col xs={12}>
                    <p className='mb-0 mt-2 text-muted'>uživatelská role</p>
                    <Form.Group controlId="formRole" className='my-2 text-muted'>
                      {Object.entries(roleLabels).map(([key, label]) => {
                        return (
                          <Form.Check
                            key={key}
                            type="radio"
                            name="role"
                            id={`role-${key}`}
                            value={key}
                            label={label === 'technik' ? 'přijímací technik' : label}
                            checked={Number(key) === Number(role)}
                            onChange={(e) => handleRoleChange(e.target.value)}
                          />
                        )
                      })}
                    </Form.Group>
                  </Col>
                  <Col xs={12} className='mt-3'>
                    <Form.Group controlId="formStav" className='text-muted'>
                      <Form.Check 
                        type="switch"
                        name="stav"
                        label="uživatelský přístup je aktivní"
                        checked={stav }
                        onChange={(e) => setStav(e.target.checked)}
                      />   
                    </Form.Group>
                  </Col>                  
                  <Col>
                  {isError && (
                    <Alert variant="danger" className="d-flex align-items-center mt-2 mb-0 small">
                      <i className="bi bi-exclamation-triangle pe-3 fs-3"></i>
                      <span>Jméno, email, uživatelské jméno v MRP a uživatelské jméno do aplikace se nesmí opakovat.</span>
                    </Alert>
                  )} 
                  </Col>                    
                </Row>     
              </Container>                     
            </Modal.Body>
            <Modal.Footer className='border-0 d-flex justify-content-start px-4 pb-3'>
              <Button variant="primary mx-0" type="submit">Uložit</Button>
              <Button variant="secondary ms-1" onClick={closeModal}>Zavřít</Button>            
            </Modal.Footer>
          </Form>
        </Modal>       
      )}
      {isModalPasswordOpen && (
        <Modal show={isModalPasswordOpen} fullscreen="md-down" onHide={closeModal} keyboard={true} size="md" aria-labelledby="contained-modal-title-vcenter" centered>
          <Modal.Header closeButton className='border-0 px-4'>
            <Modal.Title className='text-primary mt-3 lh-1'>Uživatel<br/>
              <span className='text-muted fs-6'>změna hesla</span>
            </Modal.Title>            
          </Modal.Header>
          <Form noValidate validated={validated} onSubmit={handlePasswordItem} autoComplete="off">
            <Modal.Body>              
              <Container className='px-2'>
                <Row className='g-1'>     
                  <Col xs={12}>
                    <Form.Group controlId="formUzivatel">
                      <FloatingLabel label="uživatel">
                        <Form.Control
                          type="text"
                          name='uzivatel'
                          defaultValue={selectedItem.uzivatel}
                          disabled
                          readOnly
                        />
                      </FloatingLabel>
                    </Form.Group>
                  </Col>
                  <Col xs={12}>
                    <Form.Group controlId="formHeslo">
                      <div className="input-group has-validation password-input-group">
                        <FloatingLabel label='heslo' className="flex-grow-1">
                          <Form.Control
                            type={showChangePassword ? 'text' : 'password'}
                            name='heslo'
                            autoComplete='off'
                            placeholder="Heslo"
                            minLength={8}
                            maxLength={64}
                            value={changePassword}
                            onChange={(e) => setChangePassword(e.target.value)}
                            className={validated && !isChangePasswordValid ? "is-invalid start-pill" : "start-pill"}
                            title='Uveď heslo: min. 8 znaků, malé i velké písmeno, číslo a symbol.'
                            required
                          />
                        </FloatingLabel>
                        <button
                          type="button"
                          className="btn btn-outline-secondary password-toggle-group-btn "
                          onClick={() => setShowChangePassword((prev) => !prev)}
                          aria-label={showChangePassword ? 'Skrýt heslo' : 'Zobrazit heslo'}
                        >
                          <i className={`bi ${showChangePassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                        </button>
                        <div className="invalid-feedback">Heslo musí splňovat všechny podmínky.</div>
                      </div>
                    </Form.Group>
                    <div className="password-checklist mt-2">
                      {changePasswordChecks.map((rule) => (
                        <div key={rule.id} className={`password-check-item ${rule.ok ? 'is-ok' : 'is-pending'}`}>
                          <i className={`bi ${rule.ok ? 'bi-check-circle-fill' : 'bi-x-circle'}`}></i>
                          <span>{rule.label}</span>
                        </div>
                      ))}
                    </div>
                  </Col>                
                </Row>             
              </Container>              
            </Modal.Body>
            <Modal.Footer className='border-0 d-flex justify-content-start px-4 pb-3'>
              <Button variant="primary mx-0" type="submit">Uložit</Button>
              <Button variant="secondary ms-1" onClick={closeModal}>Zavřít</Button>            
            </Modal.Footer>
          </Form>
        </Modal>         
      )} 
      {isModalUpdateOpen && (
        <Modal show={isModalUpdateOpen} fullscreen="md-down" onHide={closeModal} keyboard={true} size="md" aria-labelledby="contained-modal-title-vcenter" centered scrollable>
          <Modal.Header closeButton className='border-0 px-4'>
            <Modal.Title className='text-primary mt-3 lh-1'>Uživatel<br/>
              <span className='text-muted fs-6'>upravit záznam</span>
            </Modal.Title>            
          </Modal.Header>
          <Form noValidate validated={validated} onSubmit={handleUpdateItem} className='overflow-auto' >
            <Modal.Body> 
              <Container className='px-2'>
                <Row className='g-1'>  
                  <Col xs={12}>
                    <Form.Group controlId="formJmeno">
                      <FloatingLabel
                        label='jméno a příjmení'>
                        <Form.Control
                          type="text"                          
                          name='jmeno'
                          defaultValue={selectedItem.jmeno}
                          placeholder="Jméno a příjmení"
                          maxLength={64}
                          title='Uveď jméno a příjmení uživatele (max. 64 znaků).'
                          required
                        />
                      </FloatingLabel>
                    </Form.Group>
                  </Col>
                  <Col xs={12}>
                    <Form.Group controlId="formEmail">
                      <FloatingLabel label='email'>
                        <Form.Control
                          type="email"
                          name='email'
                          defaultValue={selectedItem.email}
                          placeholder="Email"
                          maxLength={64}
                          onBlur={(e) => {
                            e.target.value = e.target.value
                              .replace(/\s+/g, '')  
                              .toLowerCase()        
                          }}
                          title='Uveď emailovou adresu uživatele (max. 64 znaků).'
                          required
                        />
                      </FloatingLabel>
                    </Form.Group>
                  </Col>
                  <Col xs={12}>
                    <Form.Group controlId="formMRP">
                      <FloatingLabel label='uživatelské jméno MRP'>
                        <Form.Control
                          type="text"
                          name='uzivatel_mrp'
                          value={uzivatelMrp}
                          placeholder="uživatel MRP"                          
                          maxLength={20}
                          readOnly={Number(role) === 3}
                          disabled={Number(role) === 3}
                          onChange={(e) => setUzivatelMrp(e.target.value)}
                          onBlur={(e) => {
                            const formatted = e.target.value
                              .replace(/\s+/g, '')
                              .toUpperCase()
                            setUzivatelMrp(formatted)
                          }}
                          title='Uveď uživatelské jméno v MRP (max. 20 znaků).'
                          required={Number(role) !== 3}
                        />
                      </FloatingLabel>
                    </Form.Group>
                  </Col>
                  <Col xs={12}>
                    <Form.Group controlId="formUzivatel">
                      <FloatingLabel label='uživatelské jméno'>
                        <Form.Control
                          type="text"
                          name='uzivatel'
                          defaultValue={selectedItem.uzivatel}
                          placeholder="Uživatelské jméno"
                          maxLength={32}
                          onBlur={(e) => {
                            e.target.value = e.target.value
                              .replace(/\s+/g, '')   
                              .toLowerCase()         
                          }}
                          title='Uveď uživatelské jméno do aplikace (max. 32 znaků).'
                          required
                        />
                      </FloatingLabel>
                    </Form.Group>
                  </Col>     
                  <Col xs={12}>
                    <p className='mb-0 mt-2 text-muted'>uživatelská role</p>
                    <Form.Group controlId="formRole" className='my-2 text-muted'>
                      {Object.entries(roleLabels).map(([key, label]) => {
                        return (
                          <Form.Check
                            key={key}
                            type="radio"
                            name="role"
                            id={`role-${key}`}
                            value={key}
                            label={label === 'technik' ? 'přijímací technik' : label}
                            checked={Number(key) === role}
                            onChange={(e) => handleRoleChange(e.target.value)}
                          />
                        )
                      })}
                    </Form.Group>
                  </Col>                 
                  <Col xs={12} className='mt-3'>
                    <Form.Group controlId="formStav" className='text-muted'>
                      <Form.Check 
                        type="switch"
                        name="stav"
                        label="uživatelský přístup je aktivní"
                        checked={stav}
                        onChange={(e) => setStav(e.target.checked)}
                      />   
                    </Form.Group>
                  </Col>                  
                  <Col>
                  {isError && (
                    <Alert variant="danger" className="d-flex align-items-center mt-2 mb-0 small">
                      <i className="bi bi-exclamation-triangle pe-3 fs-3"></i>
                      <span>Jméno, email, uživatelské jméno v MRP a uživatelské jméno do aplikace se nesmí opakovat.</span>
                    </Alert>
                  )} 
                  </Col>                    
                </Row>     
              </Container>                     
            </Modal.Body>
            <Modal.Footer className='border-0 d-flex justify-content-start px-4 pb-3'>
              <Button variant="primary mx-0" type="submit">Uložit</Button>
              <Button variant="secondary ms-1" onClick={closeModal}>Zavřít</Button>            
            </Modal.Footer>
          </Form>
        </Modal>   
      )} 
      {isModalDeleteOpen && (
        <Modal show={isModalDeleteOpen} fullscreen="md-down" onHide={closeModal} keyboard={true} size="md" aria-labelledby="contained-modal-title-vcenter" centered>
          <Modal.Header closeButton className='border-0 px-4'>
            <Modal.Title className='text-primary mt-3 lh-1'>Uživatel<br/>
              <span className='text-muted fs-6'>odstranit záznam</span>
            </Modal.Title>            
          </Modal.Header>
          <Form onSubmit={handleDeleteItem}>
            <Modal.Body>              
              <Container className='p-2'>
                <Row className='g-1'>
                  <Col>
                    <Form.Group controlId="formJmeno">
                      <FloatingLabel label="jméno a příjmení">
                        <Form.Control
                          type="text"         
                          name="jmeno"
                          defaultValue={selectedItem.jmeno}
                          readOnly
                          disabled
                        />                        
                      </FloatingLabel>
                    </Form.Group>
                  </Col>                  
                  <Col xs={12}>
                    <Form.Group controlId="formUzivatel">
                      <FloatingLabel label="uživatelské jméno">
                        <Form.Control
                          type="text"
                          name='uzivatel'
                          defaultValue={selectedItem.uzivatel}    
                          readOnly
                          disabled                          
                        />
                      </FloatingLabel>
                    </Form.Group>
                  </Col>                    
                  <Col xs={12}>
                    {isError ? (
                      <Alert variant="danger" className="d-flex align-items-center mt-2 mb-0 small">
                        <i className="bi bi-exclamation-triangle pe-3 fs-2"></i><span>Záznam nelze odstranit. Na záznam se odkazuje řádek v jiné tabulce.</span>
                      </Alert>
                    ) : (
                      <Alert variant="warning" className="d-flex align-items-center mt-2 mb-0 small">
                        <i className="bi bi-exclamation-triangle pe-3 fs-2"></i>
                        <span>Chcete skutečně smazat záznam? Tato operace je nevratná. </span>
                      </Alert>
                    )} 
                  </Col>                          
                </Row>             
              </Container>              
            </Modal.Body>
            <Modal.Footer className='border-0 d-flex justify-content-start px-4 pb-3'>
              {isError ? (
              <>
              <Button variant="secondary ms-1" onClick={closeModal}>Zavřít</Button>
              </>
              ) : (
              <>
              <Button variant="primary mx-0" type="submit">Smazat</Button>
              <Button variant="secondary ms-1" onClick={closeModal}>Zavřít</Button>
              </>)} 
            </Modal.Footer>
          </Form>
        </Modal>         
      )} 

      <Notification notifications={notifications} setNotifications={setNotifications} />      
    </>
  )
}

export default Users





